import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { concatPhoneNumber } from "checkout/ts/redux/utils/intl";
import classnames from "classnames";
import { Countries } from "common/locale/constants";
import intlTelInput, { Iti, SomeOptions } from "intl-tel-input";
import { noop } from "lodash";
import { PhoneNumber } from "univapay-node";

type ItiProps = {
    initialValue?: PhoneNumber;
    onChange?: (value: PhoneNumber) => void;
    initOptions?: SomeOptions;
    inputProps?: Partial<React.InputHTMLAttributes<HTMLInputElement>>;
    disabled?: boolean;
    tabIndex?: number;
    className?: string;
    style?: React.CSSProperties;
    value?: PhoneNumber;
    forcedCountryCode?: string;
};

export type IntlTelInputRef = {
    getInstance: () => Iti | null;
    getInput: () => HTMLInputElement | null;
};

const getLocalNumber = (ref) => ref.current?.telInput?.value || "";
const getCountryCode = (ref) => {
    const { areaCodes, dialCode = "" } = ref.current?.getSelectedCountryData();
    return areaCodes?.length ? `${dialCode}${areaCodes[0] || ""}` : dialCode;
};

export const IntlTelInput = forwardRef(function IntlTelInput(
    {
        value,
        initialValue,
        tabIndex,
        onChange = noop,
        className,
        style,
        initOptions = {},
        inputProps = {},
        disabled = false,
        forcedCountryCode,
    }: ItiProps,
    ref: React.ForwardedRef<IntlTelInputRef>
) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const itiRef = useRef<Iti | null>(null);

    useImperativeHandle(ref, () => ({ getInstance: () => itiRef.current, getInput: () => inputRef.current }));

    useEffect(() => {
        if (!itiRef.current) {
            return;
        }

        if (value.localNumber !== getLocalNumber(itiRef)) {
            itiRef.current.telInput.value = value.localNumber;
        }

        if ((forcedCountryCode || value.countryCode) !== getCountryCode(itiRef)) {
            itiRef.current.setCountry(
                Object.values(Countries).find(({ dialCode }) => dialCode === value.countryCode)?.iso
            );
        }
    }, [value?.localNumber, value?.countryCode, forcedCountryCode]);

    const update = useCallback((): void => {
        const localNumber: string = getLocalNumber(itiRef);
        const countryCode: string = getCountryCode(itiRef);

        if (countryCode) {
            onChange({ localNumber, countryCode });
        }
    }, [onChange]);

    // Ensure the widget is updated when the inline form starts hidden
    useEffect(() => {
        if (!inputRef.current || inputRef.current?.clientWidth !== 0) {
            return;
        }

        const resizeObserver = new ResizeObserver(
            () =>
                (inputRef.current.style.paddingLeft = `${
                    document.querySelectorAll(".iti__flag-container")[0].clientWidth + 6
                }px`)
        );
        resizeObserver.observe(inputRef.current);

        return () => resizeObserver.disconnect();
    }, [inputRef.current?.clientWidth]);

    useEffect(() => {
        itiRef.current = intlTelInput(inputRef.current, initOptions);

        return () => itiRef.current?.destroy();
    }, []);

    const handlePaste = useCallback(() => setTimeout(update), [update]);

    useEffect(() => {
        const inputRefCurrent = inputRef.current;
        if (inputRefCurrent) {
            inputRefCurrent.addEventListener("countrychange", update);
            inputRefCurrent.addEventListener("paste", handlePaste);
            itiRef.current?.promise.then(update);
        }

        return () => {
            if (inputRefCurrent) {
                inputRefCurrent.removeEventListener("countrychange", update);
                inputRefCurrent.removeEventListener("paste", handlePaste);
            }
        };
    }, [update, handlePaste]);

    return (
        <input
            type="tel"
            className={classnames(className, "intl-tel-input form-control")}
            ref={inputRef}
            onInput={update}
            defaultValue={concatPhoneNumber(initialValue)}
            disabled={disabled}
            tabIndex={tabIndex}
            style={style}
            {...inputProps}
        />
    );
});
